import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLandingLanguage } from "../i18n/LandingLanguage";
import InteractiveWordmark from "../components/InteractiveWordmark";
import { useViewport } from "../hooks/useViewport";
import { trackOutboundClick } from "@/lib/tracking";
import { isNewsletterEmail, subscribeNewsletter } from "@/lib/newsletter";
import { Icon } from "./Icon";
import { ASSETS, ROUTES } from "./config";
import "./landing.css";
import "./site-chrome.css"; // [ULTURE] hovers propios (ulsc-*) de la cabecera y el pie comunes

// ─── Pie común de la web (todas las páginas salvo la home, que pinta el suyo en LandingInicio.jsx) ───
// [ULTURE] Calco del <footer> nuevo del diseño (actualización 01, CAMBIOS 2; el mismo en las 7 páginas, ver SobreNosotros.jsx):
// esquinas superiores redondeadas sobre el color de la sección anterior (prop topBg), degradado navy #13314A → #0B1B28 con halo
// teal/azul arriba, newsletter (email + «Suscribirme» y confirmación), tarjetas de Ulture Tactic y Ulture Pest con hover,
// columnas Producto · Soluciones · Empresa · Legal, dirección/teléfono/email, redes, «© 2026 Ulture · Creado por agricultores,
// para agricultores.» y la entrada de cada bloque al llegar a pantalla (data-ft / ftRef del diseño). Mismo contenedor que las
// páginas del diseño (container-type inline-size, zoom 0,85 desde 640 px, ancho 1240).
// Lo que pone la web:
//  · sin el gran «Ulture» punteado del diseño: justo debajo del pie va el wordmark interactivo de siempre (panel claro,
//    InteractiveWordmark sin cambios desde antes de la actualización 01), a petición del cliente;
//  · la newsletter se guarda de verdad (src/lib/newsletter.ts: tabla newsletter_subscribers + bienvenida + aviso a admin), como
//    la del pie anterior (FooterNewsletter.tsx); si falla, vuelve el formulario con un aviso;
//  · enlaces reales de la web (rutas internas sin recargar, soluciones, prensa, opiniones, casos de éxito, legales), redes
//    reales (se registran como salida), dirección «C/ Chillida, 4, 04720» (el diseño dice «Chirida»);
//  · textos en 7 idiomas (tabla propia; no se cargan los diccionarios de la home en todas las páginas);
//  · /apps (enlace de la bio de Instagram) sin pie.

type Lang = "es" | "en" | "it" | "fr" | "nl" | "ar" | "zh";

// Texto español EXACTO → traducción (el español se pinta tal cual).
const TXT: Record<Exclude<Lang, "es">, Record<string, string>> = {
  en: {
    "Newsletter": "Newsletter",
    "Novedades de Ulture y del campo,": "Ulture and farming news,",
    "una o dos veces al mes.": "once or twice a month.",
    "tu@email.com": "you@email.com",
    "Email": "Email",
    "Suscribirme": "Subscribe",
    "Apuntado. Te escribimos en el próximo envío.": "You’re in. See you in the next issue.",
    "Escribe un email válido.": "Enter a valid email address.",
    "No se ha podido enviar. Inténtalo de nuevo o llámanos al +34 638 68 48 96.": "We couldn't send it. Please try again or call us on +34 638 68 48 96.",
    "Tu mano de obra en el móvil, al minuto.": "Your workforce on your phone, up to the minute.",
    "Pilla la plaga a tiempo, antes de que se extienda.": "Catch pests in time, before they spread.",
    "Hecho con cariño para quienes alimentan al mundo.": "Made with care for the people who feed the world.",
    "Producto": "Product",
    "Soluciones": "Solutions",
    "Empresa": "Company",
    "Legal": "Legal",
    "Nuestras apps": "Our apps",
    "Software agrícola": "Farm software",
    "Gestión agrícola": "Farm management",
    "Control horario agrícola": "Farm time tracking",
    "Gestión de invernaderos": "Greenhouse management",
    "Control de plagas": "Pest management",
    "Cuaderno de campo digital": "Digital field notebook",
    "ERP agrícola": "Agriculture ERP",
    "Sobre nosotros": "About us",
    "Partners": "Partners",
    "Blog": "Blog",
    "Contacto": "Contact",
    "Opiniones": "Reviews",
    "Prensa": "Press",
    "Casos de éxito": "Case studies",
    "Política de Privacidad": "Privacy Policy",
    "Términos de Servicio": "Terms of Service",
    "Política de Cookies": "Cookie Policy",
    "DPA": "DPA",
    "Creado por agricultores, para agricultores.": "Built by farmers, for farmers.",
  },
  it: {
    "Newsletter": "Newsletter",
    "Novedades de Ulture y del campo,": "Novità di Ulture e dal campo,",
    "una o dos veces al mes.": "una o due volte al mese.",
    "tu@email.com": "tu@email.com",
    "Email": "Email",
    "Suscribirme": "Iscrivimi",
    "Apuntado. Te escribimos en el próximo envío.": "Iscrizione fatta. Ti scriviamo al prossimo invio.",
    "Escribe un email válido.": "Inserisci un indirizzo email valido.",
    "No se ha podido enviar. Inténtalo de nuevo o llámanos al +34 638 68 48 96.": "Invio non riuscito. Riprova o chiamaci al +34 638 68 48 96.",
    "Tu mano de obra en el móvil, al minuto.": "La tua manodopera sul telefono, minuto per minuto.",
    "Pilla la plaga a tiempo, antes de que se extienda.": "Scova i parassiti in tempo, prima che si diffondano.",
    "Hecho con cariño para quienes alimentan al mundo.": "Fatto con il cuore per chi nutre il mondo.",
    "Producto": "Prodotto",
    "Soluciones": "Soluzioni",
    "Empresa": "Azienda",
    "Legal": "Note legali",
    "Nuestras apps": "Le nostre app",
    "Software agrícola": "Software agricolo",
    "Gestión agrícola": "Gestione agricola",
    "Control horario agrícola": "Presenze e orari in campo",
    "Gestión de invernaderos": "Gestione delle serre",
    "Control de plagas": "Controllo parassiti",
    "Cuaderno de campo digital": "Quaderno di campagna digitale",
    "ERP agrícola": "ERP agricolo",
    "Sobre nosotros": "Chi siamo",
    "Partners": "Partner",
    "Blog": "Blog",
    "Contacto": "Contatti",
    "Opiniones": "Recensioni",
    "Prensa": "Stampa",
    "Casos de éxito": "Casi di successo",
    "Política de Privacidad": "Informativa sulla privacy",
    "Términos de Servicio": "Termini di servizio",
    "Política de Cookies": "Informativa sui cookie",
    "DPA": "DPA",
    "Creado por agricultores, para agricultores.": "Creato da agricoltori, per agricoltori.",
  },
  fr: {
    "Newsletter": "Newsletter",
    "Novedades de Ulture y del campo,": "Les nouveautés d’Ulture et du monde agricole,",
    "una o dos veces al mes.": "une à deux fois par mois.",
    "tu@email.com": "votre@email.com",
    "Email": "E-mail",
    "Suscribirme": "Je m’abonne",
    "Apuntado. Te escribimos en el próximo envío.": "C’est noté. Rendez-vous au prochain envoi.",
    "Escribe un email válido.": "Saisissez une adresse e-mail valide.",
    "No se ha podido enviar. Inténtalo de nuevo o llámanos al +34 638 68 48 96.": "L’envoi a échoué. Réessayez ou appelez-nous au +34 638 68 48 96.",
    "Tu mano de obra en el móvil, al minuto.": "Votre main-d’œuvre sur mobile, à la minute près.",
    "Pilla la plaga a tiempo, antes de que se extienda.": "Repérez les ravageurs à temps, avant qu’ils ne se propagent.",
    "Hecho con cariño para quienes alimentan al mundo.": "Fait avec cœur pour celles et ceux qui nourrissent le monde.",
    "Producto": "Produit",
    "Soluciones": "Solutions",
    "Empresa": "Entreprise",
    "Legal": "Légal",
    "Nuestras apps": "Nos applis",
    "Software agrícola": "Logiciel agricole",
    "Gestión agrícola": "Gestion agricole",
    "Control horario agrícola": "Pointage agricole",
    "Gestión de invernaderos": "Gestion des serres",
    "Control de plagas": "Gestion des ravageurs",
    "Cuaderno de campo digital": "Cahier de culture numérique",
    "ERP agrícola": "ERP agricole",
    "Sobre nosotros": "À propos",
    "Partners": "Partenaires",
    "Blog": "Blog",
    "Contacto": "Contact",
    "Opiniones": "Avis",
    "Prensa": "Presse",
    "Casos de éxito": "Études de cas",
    "Política de Privacidad": "Politique de confidentialité",
    "Términos de Servicio": "Conditions d’utilisation",
    "Política de Cookies": "Politique des cookies",
    "DPA": "DPA",
    "Creado por agricultores, para agricultores.": "Créé par des agriculteurs, pour les agriculteurs.",
  },
  nl: {
    "Newsletter": "Nieuwsbrief",
    "Novedades de Ulture y del campo,": "Nieuws over Ulture en de tuinbouw,",
    "una o dos veces al mes.": "een of twee keer per maand.",
    "tu@email.com": "jouw@email.nl",
    "Email": "E-mail",
    "Suscribirme": "Aanmelden",
    "Apuntado. Te escribimos en el próximo envío.": "Je bent aangemeld. Tot de volgende editie.",
    "Escribe un email válido.": "Vul een geldig e-mailadres in.",
    "No se ha podido enviar. Inténtalo de nuevo o llámanos al +34 638 68 48 96.": "Versturen is niet gelukt. Probeer het opnieuw of bel ons op +34 638 68 48 96.",
    "Tu mano de obra en el móvil, al minuto.": "Je ploeg op je telefoon, tot op de minuut.",
    "Pilla la plaga a tiempo, antes de que se extienda.": "Ontdek plagen op tijd, voordat ze zich verspreiden.",
    "Hecho con cariño para quienes alimentan al mundo.": "Met liefde gemaakt voor wie de wereld voedt.",
    "Producto": "Product",
    "Soluciones": "Oplossingen",
    "Empresa": "Bedrijf",
    "Legal": "Juridisch",
    "Nuestras apps": "Onze apps",
    "Software agrícola": "Teeltsoftware",
    "Gestión agrícola": "Teeltmanagement",
    "Control horario agrícola": "Urenregistratie in de teelt",
    "Gestión de invernaderos": "Kasmanagement",
    "Control de plagas": "Plaagbeheersing",
    "Cuaderno de campo digital": "Digitaal teeltregister",
    "ERP agrícola": "Agrarisch ERP",
    "Sobre nosotros": "Over ons",
    "Partners": "Partners",
    "Blog": "Blog",
    "Contacto": "Contact",
    "Opiniones": "Ervaringen",
    "Prensa": "Pers",
    "Casos de éxito": "Klantverhalen",
    "Política de Privacidad": "Privacybeleid",
    "Términos de Servicio": "Algemene voorwaarden",
    "Política de Cookies": "Cookiebeleid",
    "DPA": "DPA",
    "Creado por agricultores, para agricultores.": "Gemaakt door telers, voor telers.",
  },
  ar: {
    "Newsletter": "النشرة البريدية",
    "Novedades de Ulture y del campo,": "أخبار Ulture والزراعة،",
    "una o dos veces al mes.": "مرة أو مرتين في الشهر.",
    "tu@email.com": "name@email.com",
    "Email": "البريد الإلكتروني",
    "Suscribirme": "اشترك",
    "Apuntado. Te escribimos en el próximo envío.": "تم الاشتراك. نراك في العدد القادم.",
    "Escribe un email válido.": "يُرجى إدخال بريد إلكتروني صحيح.",
    "No se ha podido enviar. Inténtalo de nuevo o llámanos al +34 638 68 48 96.": "تعذّر الإرسال. حاول مرة أخرى أو اتصل بنا على ‎+34 638 68 48 96.",
    "Tu mano de obra en el móvil, al minuto.": "فريق عملك في هاتفك، دقيقة بدقيقة.",
    "Pilla la plaga a tiempo, antes de que se extienda.": "اكتشف الآفة مبكرًا، قبل أن تنتشر.",
    "Hecho con cariño para quienes alimentan al mundo.": "صُنع بحب لمن يُطعمون العالم.",
    "Producto": "المنتج",
    "Soluciones": "الحلول",
    "Empresa": "الشركة",
    "Legal": "قانوني",
    "Nuestras apps": "تطبيقاتنا",
    "Software agrícola": "البرمجيات الزراعية",
    "Gestión agrícola": "الإدارة الزراعية",
    "Control horario agrícola": "تسجيل دوام العمال الزراعيين",
    "Gestión de invernaderos": "إدارة البيوت المحمية",
    "Control de plagas": "مكافحة الآفات",
    "Cuaderno de campo digital": "دفتر الحقل الرقمي",
    "ERP agrícola": "نظام ERP زراعي",
    "Sobre nosotros": "من نحن",
    "Partners": "شركاؤنا",
    "Blog": "المدونة",
    "Contacto": "اتصل بنا",
    "Opiniones": "آراء العملاء",
    "Prensa": "الصحافة",
    "Casos de éxito": "قصص النجاح",
    "Política de Privacidad": "سياسة الخصوصية",
    "Términos de Servicio": "شروط الخدمة",
    "Política de Cookies": "سياسة ملفات الارتباط",
    "DPA": "DPA",
    "Creado por agricultores, para agricultores.": "صنعه مزارعون، من أجل المزارعين.",
  },
  zh: {
    "Newsletter": "邮件订阅",
    "Novedades de Ulture y del campo,": "Ulture 动态和田间新鲜事，",
    "una o dos veces al mes.": "每月一到两次。",
    "tu@email.com": "你的邮箱地址",
    "Email": "邮箱",
    "Suscribirme": "订阅",
    "Apuntado. Te escribimos en el próximo envío.": "订阅成功，下一期见。",
    "Escribe un email válido.": "请输入有效的邮箱地址。",
    "No se ha podido enviar. Inténtalo de nuevo o llámanos al +34 638 68 48 96.": "发送失败。请重试，或拨打 +34 638 68 48 96 联系我们。",
    "Tu mano de obra en el móvil, al minuto.": "用工情况，手机上实时掌握。",
    "Pilla la plaga a tiempo, antes de que se extienda.": "及时发现虫害，赶在扩散之前。",
    "Hecho con cariño para quienes alimentan al mundo.": "用心打造，献给养活世界的人。",
    "Producto": "产品",
    "Soluciones": "解决方案",
    "Empresa": "公司",
    "Legal": "法律条款",
    "Nuestras apps": "我们的 App",
    "Software agrícola": "农业软件",
    "Gestión agrícola": "农业管理",
    "Control horario agrícola": "农业考勤管理",
    "Gestión de invernaderos": "温室管理",
    "Control de plagas": "虫害防治",
    "Cuaderno de campo digital": "电子田间记录本",
    "ERP agrícola": "农业 ERP",
    "Sobre nosotros": "关于我们",
    "Partners": "合作伙伴",
    "Blog": "博客",
    "Contacto": "联系我们",
    "Opiniones": "用户评价",
    "Prensa": "媒体报道",
    "Casos de éxito": "成功案例",
    "Política de Privacidad": "隐私政策",
    "Términos de Servicio": "服务条款",
    "Política de Cookies": "Cookie 政策",
    "DPA": "数据处理协议",
    "Creado por agricultores, para agricultores.": "由农人打造，为农人服务。",
  },
};

// [ULTURE] solo en desarrollo (en producción import.meta.env.DEV es false y Vite quita este bloque y los JSON):
// avisa en consola si un texto de TXT que también está en la home (src/landing/inicio/i18n/<lang>.json) se traduce
// distinto, para que los dos pies no acaben diciendo cosas distintas. Si sale el aviso, iguala los dos.
let txtChecked = false;
function checkTxtAgainstHome() {
  if (txtChecked) return;
  txtChecked = true;
  const load: Record<Exclude<Lang, "es">, () => Promise<{ default: Record<string, string> }>> = {
    en: () => import("./i18n/en.json"), it: () => import("./i18n/it.json"), fr: () => import("./i18n/fr.json"),
    nl: () => import("./i18n/nl.json"), ar: () => import("./i18n/ar.json"), zh: () => import("./i18n/zh.json"),
  };
  (Object.keys(load) as Exclude<Lang, "es">[]).forEach((l) => {
    load[l]().then(({ default: home }) => {
      const bad = Object.keys(TXT[l]).filter((k) => home[k] != null && home[k] !== TXT[l][k]);
      if (bad.length) console.warn(`[SiteFooter] TXT.${l} no coincide con src/landing/inicio/i18n/${l}.json en: ${bad.map((k) => `«${k}»`).join(", ")}`);
    }).catch(() => { /* solo es un aviso de desarrollo */ });
  });
}

type FooterLink = { l: string; href: string };
const EASE = "cubic-bezier(.22,1,.36,1)";

// Estilos del diseño (inline, como en el <footer> generado)
const colTitle: React.CSSProperties = { fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "11.5px", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" };
const colLink: React.CSSProperties = { alignSelf: "flex-start", fontSize: "14.5px", lineHeight: "1.35", color: "rgba(255,255,255,0.8)", transition: `color .2s ease, transform .35s ${EASE}` };
const card: React.CSSProperties = { display: "flex", alignItems: "center", gap: "16px", minWidth: 0, padding: "clamp(16px, 1.8cqw, 22px)", borderRadius: "24px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", transition: `background-color .4s ease, border-color .4s ease, transform .5s ${EASE}` };
const social: React.CSSProperties = { width: "40px", height: "40px", boxSizing: "border-box", borderRadius: "12px", display: "grid", placeItems: "center", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", transition: `background-color .25s ease, transform .35s ${EASE}` };

export default function SiteFooter({ topBg = "transparent" }: { topBg?: string }) {
  const { lang } = useLandingLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isMobile } = useViewport();
  const rootRef = useRef<HTMLDivElement>(null);
  const footRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // [ULTURE] mismo corte que las páginas del diseño: zoom 0,85 si el contenedor mide ≥ 640 px (móvil: 1)
  const [isM, setIsM] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 640 : false));
  const Z = isM ? 1 : 0.85;
  // Newsletter: «sent» como ftSent del diseño
  const [sent, setSent] = useState(false);
  // [ULTURE] Móvil: columnas Producto · Soluciones · Empresa · Legal plegadas; cada una se abre con su flecha (igual que la home)
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = () => { const w = el.offsetWidth || window.innerWidth; setIsM(w < 640); };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pathname]);

  useEffect(() => { if (import.meta.env.DEV) checkTxtAgainstHome(); }, []);

  // [ULTURE] ftRef del diseño: los bloques del pie (data-ft) que empiezan por debajo de la pantalla entran al llegar
  // (opacidad 0,9 s + translate 36 px → 0 en 1,1 s, escalonados 90 ms). Se repite en cada página (la web no recarga).
  // El rAF deja que la página nueva suba arriba antes de mirar qué queda por debajo. «Reducir movimiento»: sin entrada.
  useEffect(() => {
    const el = footRef.current;
    let io: IntersectionObserver | null = null;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let items: HTMLElement[] = [];
    const raf = requestAnimationFrame(() => {
      if (!el) return;
      let rm = false;
      try { rm = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { /* noop */ }
      if (rm || !("IntersectionObserver" in window)) return;
      items = Array.from(el.querySelectorAll<HTMLElement>("[data-ft]")).filter((n) => n.getBoundingClientRect().top > window.innerHeight);
      if (!items.length) return;
      items.forEach((n) => { n.style.opacity = "0"; n.style.transform = "translate3d(0, 36px, 0)"; });
      io = new IntersectionObserver((ents) => {
        let k = 0;
        ents.forEach((x) => {
          if (!x.isIntersecting) return;
          const n = x.target as HTMLElement, d = k++ * 90;
          if (io) io.unobserve(n);
          n.style.transition = `opacity .9s ease ${d}ms, transform 1.1s ${EASE} ${d}ms`;
          n.style.opacity = ""; n.style.transform = "";
          timers.push(setTimeout(() => { n.style.transition = ""; }, d + 1600));
        });
      }, { threshold: 0.12, rootMargin: "0px 0px -4% 0px" });
      items.forEach((n) => io!.observe(n));
    });
    return () => {
      cancelAnimationFrame(raf);
      if (io) io.disconnect();
      timers.forEach(clearTimeout);
      items.forEach((n) => { n.style.opacity = ""; n.style.transform = ""; n.style.transition = ""; });
    };
  }, [pathname]);

  // [ULTURE] T('texto español'): idioma activo → inglés → español.
  const T = useMemo(() => {
    const d = lang === "es" ? null : TXT[lang as Exclude<Lang, "es">] || TXT.en;
    return (es: string) => (d ? (d[es] != null ? d[es] : TXT.en[es] != null ? TXT.en[es] : es) : es);
  }, [lang]);

  // [ULTURE] enlaces internos sin recargar (react-router); Cmd/Ctrl/Mayús + clic abre otra pestaña como siempre.
  const go = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const href = e.currentTarget.getAttribute("href") || "/";
    e.preventDefault();
    if (href === window.location.pathname) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    navigate(href);
  }, [navigate]);
  // [ULTURE] redes: se registran como salida igual que en la home (HomeInicio → trackOutboundClick)
  const out = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const a = e.currentTarget;
    trackOutboundClick(a.getAttribute("aria-label") || a.href, a.href);
  }, []);

  // [ULTURE] ftSubmit del diseño conectado a la newsletter real (src/lib/newsletter.ts, la misma de FooterNewsletter.tsx):
  // email no válido → aviso en el campo (en el idioma de la web) sin enviar; válido → confirmación al momento y alta en
  // newsletter_subscribers + correos. Si la base de datos lo rechaza, vuelve el formulario con el email y un aviso.
  const say = useCallback((msg: string) => {
    const inp = inputRef.current;
    if (!inp) return;
    inp.setCustomValidity(msg);
    inp.reportValidity();
    const clr = () => { inp.setCustomValidity(""); inp.removeEventListener("input", clr); };
    inp.addEventListener("input", clr);
  }, []);
  const onSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const inp = inputRef.current;
    const email = (inp?.value || "").trim();
    if (!inp) return;
    if (!isNewsletterEmail(email)) { inp.focus(); if (email) say(T("Escribe un email válido.")); return; }
    setSent(true);
    const res = await subscribeNewsletter(email, lang);
    if (res === "invalid" || res === "error") {
      setSent(false);
      setTimeout(() => say(res === "invalid" ? T("Escribe un email válido.") : T("No se ha podido enviar. Inténtalo de nuevo o llámanos al +34 638 68 48 96.")), 30);
      return;
    }
    inp.value = "";
  }, [T, lang, say]);

  // [ULTURE] /apps (enlace de la bio de Instagram): sin pie, como con FooterLanding.
  if (pathname.startsWith("/apps")) return null;

  const es = lang === "es";
  const L = (l: string, href: string): FooterLink => ({ l, href });
  /* [ULTURE] enlaces reales de la web: las columnas del diseño con las páginas que existen (soluciones, opiniones, prensa,
     casos de éxito, legales); «Software agrícola» en español va a su página en español. */
  const cols: [string, FooterLink[]][] = [
    [T("Producto"), [L("Ulture Tactic", ROUTES["Ulture Tactic"]), L("Ulture Pest", ROUTES["Ulture Pest"]), L(T("Nuestras apps"), "/#apps"), L(T("Software agrícola"), es ? "/software-agricola" : "/agricultural-management-software")]],
    [T("Soluciones"), [L(T("Gestión agrícola"), "/agricultural-management-software"), L(T("Control horario agrícola"), "/farm-operations-software"), L(T("Gestión de invernaderos"), "/greenhouse-management-software"), L(T("Control de plagas"), "/pest-management-software"), L(T("Cuaderno de campo digital"), "/cuaderno-de-campo-digital"), L(T("ERP agrícola"), "/agriculture-erp")]],
    [T("Empresa"), [L(T("Sobre nosotros"), ROUTES["Sobre Nosotros"]), L(T("Partners"), ROUTES["Partners"]), L(T("Blog"), ROUTES["Blog"]), L(T("Contacto"), ROUTES["Contacto"]), L(T("Prensa"), "/prensa"), L(T("Opiniones"), "/opiniones"), L(T("Casos de éxito"), "/personalize/case-studies")]],
    [T("Legal"), [L(T("Política de Privacidad"), "/privacy"), L(T("Términos de Servicio"), "/terms"), L(T("Política de Cookies"), "/cookie-policy"), L(T("DPA"), "/dpa")]],
  ];
  const socialIcons = [
    { name: "LinkedIn", src: ASSETS + "social-linkedin.svg", href: "https://www.linkedin.com/company/112684245/" },
    { name: "Instagram", src: ASSETS + "social-instagram.svg", href: "https://www.instagram.com/ulture.co" },
    { name: "TikTok", src: ASSETS + "social-tiktok.svg", href: "https://www.tiktok.com/@ulture.co0" },
    { name: "YouTube", src: ASSETS + "social-youtube.svg", href: "https://www.youtube.com/@UltureTechnology" },
  ];
  const apps = [
    { cls: "ulsc-h4", href: ROUTES["Ulture Tactic"], icon: "app-icon-tactic.webp", name: "Ulture Tactic", tag: T("Tu mano de obra en el móvil, al minuto.") },
    { cls: "ulsc-h5", href: ROUTES["Ulture Pest"], icon: "app-icon-pest.webp", name: "Ulture Pest", tag: T("Pilla la plaga a tiempo, antes de que se extienda.") },
  ];

  return (
    // [ULTURE] data-home-lang: fuentes de árabe/chino de landing.css §5; dir="ltr": el maquetado no se da la vuelta en árabe (como la home).
    // marginTop auto: al fondo en los envoltorios con columna flexible (SiteChrome, LandingLayout).
    <div data-site-footer="" data-home-lang={lang} dir="ltr" style={{ marginTop: "auto" }}>
      {/* [ULTURE] mismo contenedor que las páginas del diseño (container-type + zoom). topBg = el <div> que envuelve el pie en
          el diseño: el color de la sección anterior, que asoma por las esquinas redondeadas. */}
      <div ref={rootRef} data-ul-landing="" style={{ containerType: "inline-size", position: "relative", background: topBg, color: "var(--text)" } as React.CSSProperties}>
        <div style={Z < 1 ? { zoom: Z } : undefined}>
          <div style={{ fontFamily: "var(--font-body)" }}>
            <footer ref={footRef} data-screen-label="Footer" style={{ position: "relative", overflow: "hidden", color: "#fff", background: "linear-gradient(180deg, #13314A 0%, #0B1B28 clamp(260px, 30cqw, 440px))", borderRadius: "clamp(30px, 4cqw, 60px) clamp(30px, 4cqw, 60px) 0 0", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1)" }}>
              <div aria-hidden="true" style={{ position: "absolute", left: "50%", top: 0, width: "min(1100px, 92%)", height: "clamp(240px, 28cqw, 400px)", transform: "translate(-50%, -46%)", borderRadius: "50%", background: "radial-gradient(closest-side, rgba(22,194,166,0.24), rgba(43,125,224,0.12) 55%, rgba(43,125,224,0) 100%)", pointerEvents: "none" }} />
              <div style={{ position: "relative", maxWidth: "1240px", margin: "0 auto", boxSizing: "border-box", padding: "clamp(52px, 6.4cqw, 96px) clamp(20px, 4cqw, 48px) 28px" /* [ULTURE] 28 px abajo: sin ellos la línea del copyright (último bloque) quedaba fuera del pie al entrar y no aparecía */ }}>
                {/* Newsletter */}
                <div data-ft="" style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: "24px 48px" }}>
                  <div style={{ flex: "1 1 420px", minWidth: 0, maxWidth: "660px" }}>
                    {/* [ULTURE] En móvil, sin la etiqueta «Newsletter» (a petición del cliente) */}
                    {!isM && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", height: "30px", padding: "0 12px", borderRadius: "99px", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "11.5px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#5FD8BF" }}>
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#5FD8BF" }} />
                        {T("Newsletter")}
                      </div>
                    )}
                    <div style={{ marginTop: isM ? 0 : "16px", fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "clamp(28px, calc(10px + 2.6cqw), 52px)", lineHeight: "1.04", letterSpacing: "-0.045em", textWrap: "balance" } as React.CSSProperties}>
                      {T("Novedades de Ulture y del campo,")}{lang === "zh" ? "" : " "}
                      <span style={{ color: "rgba(255,255,255,0.5)" }}>{T("una o dos veces al mes.")}</span>
                    </div>
                  </div>
                  <form onSubmit={onSubmit} noValidate style={{ flex: "1 1 360px", minWidth: 0, maxWidth: "500px", boxSizing: "border-box", display: "flex", alignItems: "center", gap: "8px", padding: "6px", borderRadius: "99px", background: "#fff", boxShadow: "0 18px 40px rgba(0,0,0,0.28)" }}>
                    {/* [ULTURE] el campo y el botón se ocultan (no se desmontan) al enviar: si el alta falla vuelven con el email escrito */}
                    <input ref={inputRef} className="ulsc-nl" name="email" type="email" autoComplete="email" placeholder={T("tu@email.com")} aria-label={T("Email")} style={{ display: sent ? "none" : undefined, flex: "1", minWidth: 0, height: "52px", boxSizing: "border-box", padding: "0 18px", border: 0, borderRadius: "99px", background: "transparent", fontFamily: "inherit", fontSize: "15.5px", color: "#13314A", outline: "none" }} />
                    <button className="ulsc-a3" type="submit" style={{ display: sent ? "none" : undefined, height: "52px", padding: "0 24px", borderRadius: "99px", border: 0, background: "var(--grad-cta)", color: "#fff", fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "15px", cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 10px 22px rgba(31,125,200,0.32)", transition: "transform .15s ease" }}>
                      {T("Suscribirme")}
                    </button>
                    {sent ? (
                      <div role="status" style={{ flex: "1", minWidth: 0, display: "flex", alignItems: "center", gap: "10px", height: "52px", padding: "0 18px", fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "15px", color: "#0E8C78" }}>
                        <span style={{ width: "28px", height: "28px", borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", background: "rgba(22,194,166,0.14)" }}>
                          <Icon name="check" size={16} color="#0E8C78" stroke={2.6} />
                        </span>
                        {T("Apuntado. Te escribimos en el próximo envío.")}
                      </div>
                    ) : null}
                  </form>
                </div>
                {/* Tarjetas de las apps */}
                <div data-ft="" style={{ marginTop: "clamp(40px, 5cqw, 64px)", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: "14px" }}>
                  {apps.map((a) => (
                    <a key={a.href} className={a.cls} href={a.href} onClick={go} style={card}>
                      <img loading="lazy" decoding="async" src={ASSETS + a.icon} alt="" style={{ width: "52px", height: "52px", borderRadius: "14px", display: "block", flexShrink: 0 }} />
                      <div style={{ flex: "1", minWidth: 0 }}>
                        <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "18px", letterSpacing: "-0.02em" }}>{a.name}</div>
                        <div style={{ marginTop: "3px", fontSize: "14px", lineHeight: "1.45", color: "rgba(255,255,255,0.68)", textWrap: "pretty" } as React.CSSProperties}>{a.tag}</div>
                      </div>
                      <span style={{ width: "40px", height: "40px", borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", background: "#fff" }}>
                        <Icon name="arrowR" size={18} color="#13314A" stroke={2.4} />
                      </span>
                    </a>
                  ))}
                </div>
                {/* Marca + columnas */}
                <div data-ft="" style={{ marginTop: "clamp(44px, 5.5cqw, 72px)", paddingTop: "clamp(36px, 4cqw, 52px)", borderTop: "1px solid rgba(255,255,255,0.1)", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: isM ? "0 28px" : "36px 28px" }}>
                  <div style={{ gridColumn: "span 2", minWidth: 0, display: "flex", flexDirection: "column", gap: "14px", marginBottom: isM ? "26px" : 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <img loading="lazy" decoding="async" src={`${ASSETS}ulture-mark.webp`} alt="Ulture" style={{ width: "30px", height: "30px", display: "block" }} />
                      <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "22px", letterSpacing: "-0.03em" }}>Ulture</span>
                    </div>
                    <div style={{ maxWidth: "300px", fontSize: "14px", lineHeight: "1.55", color: "rgba(255,255,255,0.7)" }}>
                      {T("Hecho con cariño para quienes alimentan al mundo.")}
                    </div>
                    {/* [ULTURE] dirección real: «C/ Chillida, 4, 04720» (el diseño dice «C/ Chirida»); <address> para los buscadores */}
                    <address style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "6px", fontStyle: "normal", fontSize: "14px", lineHeight: "1.5", color: "rgba(255,255,255,0.7)" }}>
                      {/* [ULTURE] En móvil, sin la dirección (a petición del cliente; teléfono y email se quedan) */}
                      {!isM && <span>Edificio Carrida, C/ Chillida, 4, 04720 Aguadulce (Almería)</span>}
                      <a className="ulsc-h6" href="tel:+34638684896" dir="ltr" style={{ fontWeight: 600, color: "#fff", transition: "color .2s ease" }}>+34 638 68 48 96</a>
                      <a className="ulsc-h6" href="mailto:admin@ulture.co" style={{ fontWeight: 600, color: "#fff", transition: "color .2s ease" }}>admin@ulture.co</a>
                    </address>
                    <div style={{ marginTop: "4px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {socialIcons.map((si) => (
                        <a key={si.name} className="ulsc-h7" href={si.href} onClick={out} aria-label={si.name} target="_blank" rel="noopener noreferrer" style={social}>
                          <img loading="lazy" decoding="async" src={si.src} alt="" style={{ width: "17px", height: "17px", display: "block" }} />
                        </a>
                      ))}
                    </div>
                  </div>
                  {cols.map(([title, links]) => (
                    <div key={title} data-ft-col="" style={{ minWidth: 0, gridColumn: isM ? "1 / -1" : "auto", borderTop: isM ? "1px solid rgba(255,255,255,0.1)" : 0 }}>
                      {isM ? (
                        <button type="button" onClick={() => setOpen((o) => ({ ...o, [title]: !o[title] }))} aria-expanded={!!open[title]} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", margin: 0, padding: "17px 0", border: 0, background: "transparent", cursor: "pointer", ...colTitle, fontSize: "12.5px", color: "rgba(255,255,255,0.72)", WebkitTapHighlightColor: "transparent" }}>
                          <span>{title}</span>
                          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: "block", flexShrink: 0, transform: open[title] ? "rotate(180deg)" : "none", transition: "transform .3s cubic-bezier(.22,1,.36,1)" }}><path d="m6 9 6 6 6-6" /></svg>
                        </button>
                      ) : (
                        <div style={colTitle}>{title}</div>
                      )}
                      {/* los enlaces siguen en la página aunque estén plegados (solo ocultos) */}
                      <div style={{ marginTop: isM ? 0 : "16px", paddingBottom: isM ? "18px" : 0, display: isM && !open[title] ? "none" : "flex", flexDirection: "column", gap: "11px" }}>
                        {links.map((lk) => (
                          <a key={lk.href + lk.l} className="ulsc-h8" href={lk.href} onClick={go} style={colLink}>{lk.l}</a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pie del pie */}
                <div data-ft="" style={{ marginTop: "clamp(44px, 5.5cqw, 72px)", paddingTop: "22px", borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "8px 24px", fontSize: "13.5px", color: "rgba(255,255,255,0.55)" }}>
                  <span>© 2026 Ulture</span>
                  <span>{T("Creado por agricultores, para agricultores.")}</span>
                </div>
              </div>
            </footer>
          </div>
        </div>
      </div>
      {/* [ULTURE] El wordmark interactivo de siempre (panel claro) al final de cada página, como antes de la actualización 01
          (el cliente lo prefiere al «Ulture» punteado del pie nuevo). Fondo navy salvo los 2 últimos px, del color del panel. */}
      <div data-home-wordmark="" aria-hidden="true" style={{ background: "linear-gradient(#0B1B28, #0B1B28) top / 100% calc(100% - 2px) no-repeat, #EEF4F8", marginTop: -1, paddingTop: 1 }}>
        <InteractiveWordmark isMobile={isMobile} marginTop={`calc(${isMobile ? "48px" : "clamp(41px, 5.1vw, 61px)"} - ${28 * (isMobile ? 1 : 0.85)}px)`} />
      </div>
    </div>
  );
}
